import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { cn } from '@discover-lib/utils';
import Badge from '@discover-ui/badge';
import RestaurantCardSlide from './restaurantCardSlide';

export type RestaurantCardImageProps = {
  slug: string;
  image?: string | null;
  images?: ReadonlyArray<string | null | undefined>;
  title?: string | null;
  priority?: boolean;
  badge?: string;
  carouselEnabled?: boolean;
  onRestaurantClick?: React.ComponentPropsWithoutRef<typeof Link>['onClick'];
} & Omit<React.ComponentPropsWithoutRef<typeof Link>, 'href' | 'title' | 'onClick'>;

export default function RestaurantCardImage({
  slug,
  image,
  images = [],
  title,
  priority,
  badge,
  carouselEnabled = false,
  className,
  onRestaurantClick,
  ...linkProps
}: RestaurantCardImageProps) {
  const [loadedSources, setLoadedSources] = useState<Record<string, true>>({});
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const imageSources = useMemo(() => {
    const filteredImages = images.filter((item): item is string => !!item);
    if (filteredImages.length > 0) {
      return filteredImages;
    }

    return [image || '/images/restaurant-placeholder.jpg'];
  }, [image, images]);

  const hasCarousel = carouselEnabled && imageSources.length > 1;
  const normalizedActiveImageIndex =
    imageSources.length > 0 ? activeImageIndex % imageSources.length : 0;
  const primaryImageSource = imageSources[0];
  // Keep skeleton state anchored to the primary image so carousel transitions do not re-show loading when rotating between already rendered slides.
  const loadingAnchorImageSource = hasCarousel
    ? primaryImageSource
    : imageSources[normalizedActiveImageIndex] || primaryImageSource;
  const isLoading = !loadedSources[loadingAnchorImageSource];

  const imageSetKey = imageSources.join('|');
  useEffect(() => {
    setActiveImageIndex(0);
  }, [imageSetKey]);

  useEffect(() => {
    if (!hasCarousel) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setActiveImageIndex((current) => (current + 1) % imageSources.length);
    }, 2400);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [hasCarousel, imageSources.length]);

  const longBadge = badge && badge.length > 13;
  const activeImageSource = imageSources[normalizedActiveImageIndex] || primaryImageSource;
  const imageEntries = hasCarousel
    ? imageSources.map((imageSource, index) => ({ imageSource, index }))
    : [{ imageSource: activeImageSource, index: normalizedActiveImageIndex }];

  return (
    <Link
      href={slug}
      {...linkProps}
      onClick={(event) => {
        onRestaurantClick?.(event);
      }}
      className={cn(
        'relative flex w-32 min-w-full max-w-[29vw] flex-col items-center self-start lg:size-auto lg:max-w-none',
        className
      )}>
      <div className={cn('relative aspect-square w-full lg:w-[218px]')}>
        {imageEntries.map(({ imageSource, index }) => (
          <RestaurantCardSlide
            key={`${imageSource}-${index}`}
            imageSource={imageSource}
            title={title}
            isActive={!hasCarousel || index === activeImageIndex}
            isPriority={priority && index === 0}
            onLoad={(loadedSource) => {
              setLoadedSources((current) => ({ ...current, [loadedSource]: true }));
            }}
          />
        ))}
      </div>
      <div
        className={cn(
          'absolute inset-0 z-10 rounded bg-gray-200 transition-opacity duration-500',
          !isLoading && 'opacity-0'
        )}>
        <div className="absolute inset-0 animate-pulse rounded bg-gray-100" />
      </div>
      {!!badge && (
        <Badge
          kind="secondary"
          className={cn(
            'xs:text-2xs absolute -bottom-2 left-1/2 z-10 -translate-x-1/2 text-center text-[9px] leading-tight lg:-bottom-4 lg:text-base',
            !longBadge && 'whitespace-nowrap',
            longBadge && 'w-max max-w-[calc(100%-0.5rem)]'
          )}>
          {badge}
        </Badge>
      )}
    </Link>
  );
}
